/**
 * Development-only i18n configuration
 *
 * This configuration is only applied in development mode to help debug
 * translation issues without causing problems in production.
 *
 * Usage in your i18n.js files:
 *
 * ```javascript
 * import { getDevConfig } from "@sisuwellness/utilities/i18n/devConfig";
 *
 * i18n.init({
 *     // ... other config
 *     ...getDevConfig("members-portal-web")
 * });
 * ```
 */

/**
 * Enhanced missing key handler that provides detailed debugging information
 *
 * @param {string} projectName - Name of the project for logging (e.g., "station-main-screen")
 * @returns {Function} The missingKeyHandler function
 */
function createMissingKeyHandler(projectName) {
    return (lngs, ns, key, fallbackValue) => {
        // Detect if the "key" looks like it's already translated text
        const looksLikeTranslatedText =
            key.length > 50 || // Very long strings are usually full sentences
            (key.includes(" ") && !key.includes(".")) || // Sentences without dots (not keys)
            /^[A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞ\s]+$/.test(key); // ALL CAPS text (like "RECOMMANDATION")

        // Detect if it looks like a translation key
        const looksLikeKey =
            key.includes(".") && // Has namespace dots
            !key.includes(" ") && // No spaces
            key.length < 100; // Reasonable key length

        // Build a helpful warning message
        let warning = `🌐 Missing translation in ${projectName}:`;
        warning += `\n  ❌ Key: "${key}"`;
        warning += `\n  🌍 Languages tried: ${lngs.join(" → ")}`;
        warning += `\n  📝 Fallback shown: "${fallbackValue || key}"`;

        // Add diagnostic information
        if (looksLikeTranslatedText && !looksLikeKey) {
            warning += `\n  ⚠️  WARNING: This looks like already-translated text, not a translation key!`;
            warning += `\n      You may be calling t() on text that's already translated.`;
            warning += `\n      Check your code for double-translation (calling t() twice on the same text).`;
        } else if (looksLikeKey) {
            warning += `\n  💡 This looks like a valid translation key.`;
            warning += `\n      Check that this key exists in SimpleLocalize for languages: ${lngs.join(", ")}`;
        }

        // Add namespace info if available
        if (ns && ns !== "translation") {
            warning += `\n  📦 Namespace: ${ns}`;
        }

        // Add stack trace info to help find where this is being called from
        const stack = new Error().stack;
        const callerLine = stack?.split("\n")?.[3]?.trim(); // Get the 3rd line (actual caller)
        if (callerLine) {
            warning += `\n  📍 Called from: ${callerLine}`;
        }

        // eslint-disable-next-line no-console
        console.group(warning);

        // Add suggestions based on the type of issue
        if (looksLikeTranslatedText && !looksLikeKey) {
            // eslint-disable-next-line no-console
            console.info("💡 Suggested fix:");
            // eslint-disable-next-line no-console
            console.info("   1. Check if you're storing translated text in state/props");
            // eslint-disable-next-line no-console
            console.info("   2. Store translation KEYS instead, then translate when rendering");
            // eslint-disable-next-line no-console
            console.info("   3. Remove the t() call if the text is already translated");
        }

        // eslint-disable-next-line no-console
        console.groupEnd();
    };
}

/**
 * Get development configuration for i18next
 *
 * Only includes saveMissing and missingKeyHandler when NODE_ENV === "development"
 * to prevent infinite loops and performance issues in production.
 *
 * @param {string} projectName - Name of the project (e.g., "station-main-screen", "members-portal-web")
 * @returns {Object} Configuration object to spread into i18n.init()
 *
 * @example
 * // In your i18n.js file:
 * import { getDevConfig } from "@sisuwellness/utilities/i18n/devConfig";
 *
 * i18n.init({
 *     fallbackLng,
 *     load: "currentOnly",
 *     // ... other config
 *     ...getDevConfig("station-main-screen") // Add dev config
 * });
 */
export function getDevConfig(projectName = "unknown-project") {
    if (process.env.NODE_ENV !== "development") {
        return {}; // Return empty object in production
    }

    return {
        saveMissing: true,
        missingKeyHandler: createMissingKeyHandler(projectName)
    };
}

/**
 * Enhanced debug configuration for i18next
 *
 * Returns debug mode setting based on environment variable or explicit flag
 *
 * @param {boolean} [explicitDebug] - Explicitly enable/disable debug (overrides env var)
 * @returns {boolean} Whether debug mode should be enabled
 *
 * @example
 * i18n.init({
 *     debug: getDebugMode(process.env.REACT_APP_I18N_DEBUG),
 *     // ... other config
 * });
 */
// env vars and config.json values arrive as strings, so Boolean("false") - which is `true` -
// would turn debug logging on for every station whose config explicitly sets it to "false".
function isTruthyFlag(value) {
    if (typeof value === "string") {
        return value.trim().toLowerCase() === "true";
    }

    return Boolean(value);
}

export function getDebugMode(explicitDebug) {
    if (explicitDebug !== undefined) {
        return isTruthyFlag(explicitDebug);
    }

    // Check for common environment variables
    return (
        isTruthyFlag(process.env.REACT_APP_I18N_DEBUG) ||
        isTruthyFlag(process.env.REACT_APP_SIMPLE_LOCALIZE_DEBUG) ||
        isTruthyFlag(process.env.I18N_DEBUG)
    );
}
